import { useEffect, useRef } from 'react'

import { onPageResume, type PageResumeListener } from '../lib/pageResume.js'

/**
 * Run `listener` whenever the page resumes from a hidden / frozen / offline
 * state (see `lib/pageResume.ts` for what counts). The latest `listener` is
 * always the one invoked, and the subscription is created once per
 * `minHiddenMs` — so passing an inline closure never re-subscribes.
 *
 * @param listener - Called with the resume reason and how long the page was hidden.
 * @param minHiddenMs - Ignore resumes after a shorter absence (`online` always fires).
 */
export function usePageResume(listener: PageResumeListener, minHiddenMs = 0): void {
  const listenerRef = useRef(listener)
  listenerRef.current = listener
  useEffect(() => onPageResume((info) => listenerRef.current(info), minHiddenMs), [minHiddenMs])
}
